// /app/frontend/src/components/SkipTracePanel.jsx
//
// Skip Trace UI — modal that runs a single-lead skip trace, shows the results,
// lets the user copy any number / email or promote a traced phone to be the
// primary lead phone. Designed to be triggered from the Lead edit modal or
// the Leads table row actions. Reads tier quota from /api/skip-trace/quota
// so it can render either a "Run trace" CTA or an "Upgrade to Hustler"
// gate (free + pro tiers).

import { useEffect, useState } from "react";
import { Loader2, Search, Phone, Mail, Users, Copy, Star, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * @param {{ leadId: string, leadAddress?: string, onClose: () => void, onPhonePromoted?: (phone: string) => void }} props
 */
export default function SkipTracePanel({ leadId, leadAddress, onClose, onPhonePromoted }) {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [promoting, setPromoting] = useState(null);

  // Initial load — fetch quota + past traces in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [q, h] = await Promise.all([
          api.get("/skip-trace/quota").then((r) => r.data),
          api.get(`/skip-trace/history/${leadId}`).then((r) => r.data).catch(() => ({ traces: [] })),
        ]);
        if (!cancelled) {
          setQuota(q);
          setHistory(h.traces || []);
          // Auto-show the most recent result for context
          if (h.traces?.length) setResult(h.traces[0].result);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load skip-trace status.");
      }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  const runTrace = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/skip-trace/leads/${leadId}`);
      setResult(data.result);
      setQuota(data.quota);
      // Optimistically prepend to history
      setHistory((h) => [{
        id: data.trace_id, lead_id: leadId, result: data.result,
        source: data.result.source, phone_count: data.result.phones.length,
        confidence: data.result.confidence,
        created_at: new Date().toISOString(),
      }, ...h]);
      toast.success(`Found ${data.result.phones.length} phone${data.result.phones.length === 1 ? "" : "s"} · ${data.quota.remaining} credits left`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Skip trace failed.";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const promotePhone = async (phone) => {
    setPromoting(phone);
    try {
      await api.post("/skip-trace/set-primary-phone", { lead_id: leadId, phone });
      toast.success("Primary phone updated on the lead.");
      onPhonePromoted?.(phone);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update phone.");
    } finally {
      setPromoting(null);
    }
  };

  const copy = (text, label = "Copied") => {
    navigator.clipboard?.writeText(text).then(() => toast.success(`${label} → clipboard`));
  };

  const tierLocked = quota && quota.cap === 0;
  const noCredits = quota && quota.cap > 0 && quota.remaining === 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="skip-trace-modal">
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <Search size={18} className="text-amber-700" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-bold text-lg leading-tight">Skip Trace</h2>
              <p className="text-xs text-[var(--ink-2)] truncate">{leadAddress || "Find the owner's contact info"}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="skip-trace-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Quota / tier status */}
          {quota && (
            <QuotaBanner quota={quota} tierLocked={tierLocked} noCredits={noCredits} />
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm" data-testid="skip-trace-error">
              {error}
            </div>
          )}

          {/* Action row */}
          {!tierLocked && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn-money flex-1"
                onClick={runTrace}
                disabled={loading || noCredits}
                data-testid="skip-trace-run"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                {loading ? "Tracing…" : history.length > 0 ? "Run another trace (1 credit)" : "Run skip trace (1 credit)"}
              </button>
            </div>
          )}

          {tierLocked && (
            <a
              href="/upgrade"
              className="btn-money w-full flex items-center justify-center"
              data-testid="skip-trace-upgrade-cta"
            >
              Upgrade to Hustler — unlock 50 credits/mo
            </a>
          )}

          {/* Results */}
          {result && (
            <ResultBlock
              result={result}
              onCopy={copy}
              onPromote={promotePhone}
              promoting={promoting}
            />
          )}

          {/* History */}
          {history.length > 1 && (
            <div className="pt-4 border-t border-[var(--line)]">
              <h4 className="text-xs font-bold uppercase tracking-wide text-[var(--ink-2)] mb-2">
                Trace history ({history.length})
              </h4>
              <div className="space-y-1 text-xs text-[var(--ink-2)]" data-testid="skip-trace-history">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between">
                    <span>{new Date(h.created_at).toLocaleString()}</span>
                    <span>{h.phone_count || 0} phones · {Math.round((h.confidence || 0) * 100)}% conf</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!quota?.vendor_live && quota && (
            <div className="text-[10px] text-[var(--ink-3)] text-center pt-2 border-t border-[var(--line)]" data-testid="skip-trace-mock-banner">
              Demo mode — vendor not yet connected. Results are deterministic test data.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuotaBanner({ quota, tierLocked, noCredits }) {
  const pct = quota.cap > 0 ? Math.round((quota.used / quota.cap) * 100) : 0;
  if (tierLocked) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3" data-testid="skip-trace-tier-locked">
        <div className="font-semibold text-sm mb-1">Skip trace is a Hustler-tier feature</div>
        <p className="text-xs">Unlock 50 traces/mo + DNC scrub + bulk-trace from the Leads table.</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-[var(--line)] px-4 py-3 bg-[var(--surface-2)]">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-semibold uppercase tracking-wide">This month's credits</span>
        <span className={noCredits ? "text-red-600 font-bold" : "text-[var(--ink-2)]"}>
          {quota.used} / {quota.cap} used
        </span>
      </div>
      <div className="h-2 bg-white rounded-full overflow-hidden" data-testid="skip-trace-quota-bar">
        <div
          className="h-full transition-all"
          style={{
            width: `${pct}%`,
            background: noCredits ? "#DC2626" : pct > 80 ? "#F59E0B" : "#10B981",
          }}
        />
      </div>
      <div className="text-[10px] text-[var(--ink-3)] mt-1.5">
        Resets {new Date(quota.resets_on).toLocaleDateString()}
      </div>
    </div>
  );
}

function ResultBlock({ result, onCopy, onPromote, promoting }) {
  return (
    <div className="space-y-4" data-testid="skip-trace-results">
      {/* Phones */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wide text-[var(--ink-2)] mb-2 flex items-center gap-1.5">
          <Phone size={11} /> Phones ({result.phones?.length || 0})
        </h4>
        {result.phones?.length ? (
          <div className="space-y-1.5">
            {result.phones.map((p) => (
              <PhoneRow
                key={p.number}
                phone={p}
                onCopy={() => onCopy(p.number, "Phone number")}
                onPromote={() => onPromote(p.number)}
                promoting={promoting === p.number}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--ink-3)] italic">No phones returned for this owner.</p>
        )}
      </div>

      {/* Emails */}
      {result.emails?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wide text-[var(--ink-2)] mb-2 flex items-center gap-1.5">
            <Mail size={11} /> Emails ({result.emails.length})
          </h4>
          <div className="space-y-1.5">
            {result.emails.map((e) => (
              <div key={e.address} className="flex items-center justify-between bg-[var(--surface-2)] rounded-lg px-3 py-2 text-sm" data-testid={`skip-trace-email-${e.address}`}>
                <div className="min-w-0 truncate">
                  <span className="font-mono">{e.address}</span>
                  <span className="text-xs text-[var(--ink-3)] ml-2">{Math.round(e.confidence * 100)}%</span>
                </div>
                <button type="button" className="btn-ghost" onClick={() => onCopy(e.address, "Email")}>
                  <Copy size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Relatives */}
      {result.relatives?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wide text-[var(--ink-2)] mb-2 flex items-center gap-1.5">
            <Users size={11} /> Known relatives ({result.relatives.length})
          </h4>
          <div className="space-y-1 text-sm">
            {result.relatives.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-[var(--ink-2)]">
                <span>{r.name}</span>
                <span className="text-xs">{r.relationship} · {Math.round(r.confidence * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PhoneRow({ phone, onCopy, onPromote, promoting }) {
  const conf = Math.round(phone.confidence * 100);
  const confColor = conf >= 80 ? "text-emerald-700" : conf >= 60 ? "text-amber-700" : "text-[var(--ink-3)]";
  return (
    <div className="flex items-center justify-between bg-[var(--surface-2)] rounded-lg px-3 py-2" data-testid={`skip-trace-phone-${phone.number}`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono text-sm tabular-nums">{formatPhone(phone.number)}</span>
        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white border border-[var(--line)] text-[var(--ink-2)]">
          {phone.label}
        </span>
        {phone.dnc && (
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-800 flex items-center gap-1" title="National Do-Not-Call registry">
            <ShieldAlert size={9} /> DNC
          </span>
        )}
        <span className={`text-xs ${confColor}`}>{conf}%</span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button type="button" className="btn-ghost" onClick={onCopy} title="Copy" data-testid={`skip-trace-copy-${phone.number}`}>
          <Copy size={12} />
        </button>
        <button
          type="button"
          className="btn-ghost"
          onClick={onPromote}
          disabled={promoting}
          title="Set as primary phone on lead"
          data-testid={`skip-trace-promote-${phone.number}`}
        >
          {promoting ? <Loader2 size={12} className="animate-spin" /> : <Star size={12} />}
        </button>
      </div>
    </div>
  );
}

function formatPhone(e164) {
  // +12046613458 -> (204) 661-3458
  const d = (e164 || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return e164;
}
